import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import SVG from "react-inlinesvg";
import { Form, Formik, FormikProps, useFormikContext } from "formik";
import usePostMyDashboardLinkUrlSite from "@/features/my-dashboard/hooks/usePostMyDashboardLinkUrlSite";
import useIdentity from "@/features/ui/hooks/use-identity";
import { myDashboardApi } from "@/features/my-dashboard/api";
import { middleTruncate } from "@/features/my-dashboard/utils";

interface MyDashboardLinkageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  businessRegistration: File | null;
  url: string;
  memo: string;
  agreement: boolean;
}

function MyDashboardLinkageModal({
  isOpen,
  onClose,
}: MyDashboardLinkageModalProps) {
  const identity = useIdentity();
  const { linkUrlSite } = usePostMyDashboardLinkUrlSite(identity?.company?.id);

  return (
    <Formik<FormValues>
      initialValues={{
        businessRegistration: null,
        url: "",
        memo: "",
        agreement: false,
      }}
      onSubmit={async (data) => {
        const { data: file } = await myDashboardApi.uploadUnoFile(
          data.businessRegistration!
        );
        linkUrlSite({
          shopping_mall_url: data.url,
          sync_shopping_mall_memo: data.memo,
          business_registration_certificate_file: {
            id: file.id,
            name: file.name,
          },
        });
        onClose();
      }}
    >
      {({ values, handleChange }) => {
        const isValid =
          values.businessRegistration && values.url && values.agreement;
        return (
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent
              borderRadius={"8px"}
              overflow={"hidden"}
              minWidth={"480px"}
            >
              <Form>
                <ModalHeader background={"#F7F9FA"} padding={"12px 16px"}>
                  <Text textStyle={"body-md-bold"} color={"#444444"}>
                    연동하기
                  </Text>
                </ModalHeader>

                <ModalBody display="flex" flexDirection={"column"} gap={"16px"}>
                  <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
                    연동을 위해 아래 필수 정보를 입력해 주세요.
                  </Text>

                  <BusinessRegistrationField />

                  {/* --- 웹사이트 주소 --- */}
                  <Box>
                    <FormControl isRequired={true}>
                      <FormLabel display={"flex"}>
                        <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
                          웹사이트 주소
                        </Text>
                      </FormLabel>
                      <Input
                        name="url"
                        value={values.url}
                        onChange={handleChange}
                        padding={"8px 12px"}
                        placeholder={"주소 입력 (예 : https://pathrade.com/)"}
                        fontSize={"14px"}
                        fontWeight={400}
                        color={"#363636"}
                        border={"none"}
                        boxShadow={"0 0 0 0.5px #8F8F8C"}
                        maxLength={2048}
                        noOfLines={1}
                      />
                    </FormControl>
                  </Box>

                  {/* --- 메모 --- */}
                  <Box>
                    <FormControl>
                      <FormLabel display={"flex"}>
                        <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
                          메모
                        </Text>
                      </FormLabel>
                      <Textarea
                        name="memo"
                        value={values.memo}
                        onChange={handleChange}
                        padding={"8px 12px"}
                        placeholder={
                          "자사몰이 아닌 경우, 샵을 확인할 수 있는 고유 정보를 추가로 입력해 주세요.\n(예 : 스토어명, 샵 ID, 판매자 ID 등)"
                        }
                        fontSize={"13px"}
                        fontWeight={400}
                        color={"#363636"}
                        border={"none"}
                        boxShadow={"0 0 0 0.5px #8F8F8C"}
                        height={"120px"}
                        resize={"none"}
                        maxLength={2000}
                      />
                    </FormControl>
                  </Box>

                  {/* --- 동의 체크박스 --- */}
                  <Flex justifyContent={"space-between"}>
                    <FormControl>
                      <FormLabel
                        display={"flex"}
                        gap={"8px"}
                        m={0}
                        width={"fit-content"}
                      >
                        <Checkbox
                          name="agreement"
                          variant={"sj_design"}
                          isChecked={values.agreement}
                          onChange={handleChange}
                        />
                        <Text textStyle={"body-sm-regular"} color={"#444444"}>
                          웹사이트 주소를 통한 데이터 수집 및 이용에 동의합니다.
                        </Text>
                      </FormLabel>
                    </FormControl>
                    <Link
                      href={
                        "https://terms.pathrade.com/files/ko/UNOTradingCorp-Pathrade-Personal-information-collection-and-use-consent-form(dashboard)-v1.0-20251127.pdf"
                      }
                      isExternal={true}
                      textStyle={"caption-bold"}
                      whiteSpace={"nowrap"}
                      color={"#444444"}
                      textDecoration={"underline"}
                    >
                      보기
                    </Link>
                  </Flex>
                </ModalBody>

                <ModalFooter
                  display={"flex"}
                  justifyContent={"space-between"}
                  gap={"12px"}
                  paddingLeft={"16px"}
                >
                  <Flex justifyContent={"end"} gap="12px" width={"100%"}>
                    <AppButton
                      variant={"normal"}
                      type="button"
                      onClick={onClose}
                    >
                      <Text textStyle={"body-sm-bold"} color={"#444444"}>
                        취소
                      </Text>
                    </AppButton>
                    <AppButton
                      variant={"primary"}
                      type="submit"
                      isDisabled={!isValid}
                    >
                      <Text textStyle={"body-sm-bold"} color={"#FFFFFF"}>
                        연동 요청
                      </Text>
                    </AppButton>
                  </Flex>
                </ModalFooter>
              </Form>
            </ModalContent>
          </Modal>
        );
      }}
    </Formik>
  );
}

export default MyDashboardLinkageModal;

const iconPath = "/icons-v1_1/";

const fileTypeImgMap: Record<string, string> = {
  pdf: iconPath + "file-pdf.svg",
  jpg: iconPath + "file-jpg.svg",
  jpeg: iconPath + "file-jpg.svg",
  png: iconPath + "file.svg",
};

const ACCEPT = "application/pdf,image/*";

const isAcceptableFile = (file: File) => {
  // MIME 타입 기준으로 체크
  return file.type === "application/pdf" || file.type.startsWith("image/");
};

const BusinessRegistrationField = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const { values, setFieldValue, resetForm } = useFormikContext<FormValues>();
  const file = values.businessRegistration;

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!isAcceptableFile(selected)) {
      alert("pdf, jpg, jpeg, png 파일만 업로드할 수 있습니다.");
      return;
    }
    setFieldValue("businessRegistration", selected);
  };

  const clearFile = () => {
    setFieldValue("businessRegistration", null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;

    if (!dropped.type.match(/pdf|image/)) {
      alert("pdf, jpg, jpeg, png 파일만 업로드할 수 있습니다.");
      return;
    }
    setFieldValue("businessRegistration", dropped);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  return (
    <Flex flexDirection={"column"} gap={"6px"}>
      <FormControl isRequired={true}>
        <FormLabel display={"flex"}>
          <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
            사업자등록증
          </Text>
        </FormLabel>
        {file ? (
          <Flex
            borderRadius={"4px"}
            boxShadow={"0 0 0 0.5px #8F8F8C"}
            padding={"6px 8px"}
            justifyContent={"space-between"}
          >
            <HStack spacing={"6px"}>
              <SVG
                src={fileTypeImgMap[file.type.split("/").at(1) ?? "png"]}
                width={24}
                height={24}
              />
              <Text textStyle={"body-sm-bold"}>
                {middleTruncate(file.name, 50)}
              </Text>
            </HStack>
            <HStack spacing={"8px"}>
              <Box
                cursor={"pointer"}
                borderRadius={"4px"}
                padding={"2px"}
                _hover={{
                  background: "#C0C0C0",
                }}
                onClick={() => inputRef.current?.click()}
              >
                <SVG
                  src={iconPath + "edit.svg"}
                  width={24}
                  height={24}
                  color={"#8F8F8C"}
                />
              </Box>
              <Box
                cursor={"pointer"}
                borderRadius={"4px"}
                padding={"2px"}
                _hover={{
                  background: "#C0C0C0",
                }}
                onClick={clearFile}
              >
                <SVG
                  src={iconPath + "trash.svg"}
                  width={24}
                  height={24}
                  color={"#8F8F8C"}
                />
              </Box>
            </HStack>
          </Flex>
        ) : (
          <Flex
            borderRadius={"4px"}
            border={isDragOver ? "1px solid #1272EF" : "1px dashed #8F8F8C"}
            justifyContent={"center"}
            alignItems={"center"}
            padding={"12px 16px"}
            background={isDragOver ? "#F0F5FF" : "transparent"}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Flex
              flexDirection={"column"}
              gap={"6px"}
              textStyle={"body-md-regular"}
              color={"#6F6F6C"}
              alignItems={"center"}
            >
              <Box textAlign={"center"}>
                <Text>여기에 파일을 끌어다 놓으세요.</Text>
                <Text>또는</Text>
              </Box>
              <Button
                borderRadius={"4px"}
                border={"1px solid #8F8F8C"}
                padding={"4px 8px"}
                height={"auto"}
                background={"#FCFCFB"}
                onClick={() => inputRef.current?.click()}
                // 실제로는 input[types=file]을 숨겨두고 트리거하는 방식으로 연결
              >
                <Text textStyle={"body-sm-bold"} color={"#444444"}>
                  파일 선택하기
                </Text>
              </Button>
            </Flex>
          </Flex>
        )}
        <input
          ref={inputRef}
          type="file"
          style={{ display: "none" }}
          accept={ACCEPT}
          onChange={handleFileSelect}
        />
      </FormControl>
      <Flex gap={"4px"}>
        <SVG
          src={"/icons-v1_1/info-circle.svg"}
          color={"#8F8F8C"}
          width={16}
          height={16}
        />
        <Text textStyle={"caption-regular"} color={"#6F6F6C"}>
          이미지(pdf, jpg, jpeg, png) 파일만 업로드할 수 있습니다.
        </Text>
      </Flex>
    </Flex>
  );
};
